/* ============================================================================
 * 登录页（A4 · 舞台页）
 *
 * 这个页面存在本身就是一条纪律：在它之前，「我是谁」是顶栏一个下拉框，
 * 选中即生效、服务端不校验，审批留痕里的操作人是客户端自己填的字符串。
 * 现在角色由服务端令牌决定，前端无法自封。
 *
 * 版式参照 xinyuan-srm 登录页复刻（两边同出 Sunstone2030renew 令牌体系）：
 * 左舞台讲「这是什么系统」，右表单只做一件事（进去）。
 * 继承的是**要素**——舞台构图、名册卡的选中语言、错落入场；
 * 不继承**形态**——SRM 那边 11px/12.5px 的硬编码字号在 V3 是已修缺陷
 * （--fs-min:12px 下限），一律换回本项目的字号令牌。
 *
 * 演示账号明示在页面上——因为它们本来就写在源码里，藏起来只会制造
 * 「这是真凭据」的错觉。上线前必须全部更换，页面也如实这么写。
 * ========================================================================== */
import { useEffect, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/primitives';
import { authMeta, health, type AuthMeta } from '@/lib/api';
import { FACTORIES, PROCESSES, ROLES, type RoleCode } from '@/domain/master';
import { useAuth } from '@/store/auth';

/** 演示岗位。名称/职责一律取自 ROLES，不在这里重抄一遍——抄一遍就会漂移。 */
const DEMO_ACCOUNTS: { user: string; pw: string; role: RoleCode }[] = [
  { user: 'shiyu', pw: 'caps-demo-ops', role: 'OPS' },
  { user: 'planner', pw: 'caps-demo-planner', role: 'PLANNER' },
  { user: 'langjing', pw: 'caps-demo-president', role: 'PRESIDENT' },
  { user: 'admin', pw: 'caps-demo-admin', role: 'ADMIN' },
];

/** 左舞台的能力序列。每一条都对得上一个真实模块，不写做不到的事。 */
const RINGS = [
  { n: '01', t: '需求计划', d: '预测分档到品种，给出置信区间而非单点' },
  { n: '02', t: '月度基线', d: '产销协同一次拍板，基线定了才排产' },
  { n: '03', t: '智能派单', d: '多基地择优，约束求解而非人工试算' },
  { n: '04', t: '车间排程', d: '排到工序与设备，换产与检修一并算进去' },
  { n: '05', t: '决策追溯', d: '每一版排产可回放，留痕认服务端身份' },
];

export default function Login() {
  const doLogin = useAuth((s) => s.login);
  // 口令跟着账号一起预填。原先只预填账号、口令留空，于是首屏那张卡看着是「已选中」、
  // 点「进入系统」却必然失败——选中态在说谎。演示口令本来就明写在这一页上，预填零损失。
  const [u, setU] = useState(DEMO_ACCOUNTS[0].user);
  const [p, setP] = useState(DEMO_ACCOUNTS[0].pw);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [offline, setOffline] = useState(false);
  const [meta, setMeta] = useState<AuthMeta | null>(null);

  useEffect(() => {
    void (async () => {
      const h = await health();
      const down = h.status === 'offline';
      setOffline(down);
      if (!down) setMeta(await authMeta().catch(() => null));
    })();
  }, []);

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBusy(true); setErr('');
    try {
      await doLogin(u.trim(), p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="loginwrap">
      <aside className="stage">
        <div className="rise rise-1">
          <div className="brand">
            <div className="embermark" aria-hidden="true">索</div>
            <div className="col" style={{ gap: 0 }}>
              <div className="brand-name">C+APS <span style={{ color: 'var(--clay-text)' }}>V3</span></div>
              <div className="brand-sub">索通智能计划与排产平台</div>
            </div>
          </div>

          {/* 视觉上是主角，语义上只是一段品牌陈述——真 h1 在右栏表单里，
              窄屏整列隐藏时标题层级也不会缺一块。不加 aria-hidden：这是内容不是装饰。 */}
          <p className="stage-title">
            需求到工序<br />排产可求解<br />每版可回放
          </p>
          <p className="stage-lede">
            面向预焙阳极（电解铝配套炭素材料）制造场景：{FACTORIES.length} 个生产基地、
            {PROCESSES.length} 道工序，焙烧是全链瓶颈，高硫料只有部分基地吃得下，
            换产与检修窗口刚性。系统把「需求 → 月度基线 → 派单 → 排程 → 审批 → 下发 → 追溯」
            建成可求解、可复现、可追责的闭环。
          </p>
        </div>

        <div className="rise rise-3 col" style={{ gap: 'var(--sp-6)' }}>
          <div className="ringlist">
            {RINGS.map((r) => (
              <div key={r.t} className="it">
                <span className="ix num">{r.n}</span>
                <span className="tt">{r.t}</span>
                <span className="dd">{r.d}</span>
              </div>
            ))}
          </div>

          <div className="stage-foot">
            <span className="kilnmark" aria-hidden="true"><i /><i /><i /><i /><i /></span>
            <div className="t-aux">
              索通发展股份有限公司 · 预焙阳极 / 煅后焦 / 锂电负极 / 薄膜电容器多业态
              <br />主数据（基地 · 产品 · 客户 · 成本）取自真实企业档案；
              <span style={{ color: 'var(--gold-text)' }}>订单、排产与求解结果为演示数据。</span>
            </div>
          </div>
        </div>
      </aside>

      <div className="form">
        {offline ? (
          <div className="col rise rise-1" style={{ gap: 'var(--sp-3)' }}>
            <div className="eyebrow">登录不可用</div>
            <h1 className="form-title">后端未连接</h1>
            <p className="t-aux">
              身份认证在服务端。后端不在时既不能签发令牌、也无法校验权限，
              因此这里不提供「跳过登录」——那等于把角色重新交回给浏览器自己声明，
              正是这一版要修掉的问题。
            </p>
            <p className="t-aux">
              启动后端：<code className="mono">cd api &amp;&amp; .venv/Scripts/python.exe -m uvicorn app.main:app --port 8188</code>
            </p>
            <div>
              <Button variant="secondary" icon="radar" onClick={() => window.location.reload()}>重新检测</Button>
            </div>
          </div>
        ) : (
          <form className="col" style={{ gap: 'var(--sp-5)' }} onSubmit={submit}>
            <div className="rise rise-1">
              <div className="eyebrow">登录</div>
              <h1 className="form-title">选择一个岗位进入</h1>
              <p className="t-aux" style={{ marginTop: 'var(--sp-2)' }}>
                {DEMO_ACCOUNTS.length} 个角色看到的导航、可执行动作与审批权完全不同。
                权限由服务端强制——前端隐藏按钮只是少显示一个按钮，不构成边界。
              </p>
            </div>

            <div className="roster rise rise-2" role="radiogroup" aria-label="选择演示岗位">
              <div className="grouphd">演示岗位 · 点选即填入凭据</div>
              {DEMO_ACCOUNTS.map((a) => {
                const r = ROLES[a.role];
                const on = u === a.user;
                return (
                  <button
                    key={a.user}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`acct${on ? ' on' : ''}`}
                    onClick={() => { setU(a.user); setP(a.pw); }}
                  >
                    <span className="av" aria-hidden="true">{r.person.slice(0, 1)}</span>
                    <span className="acct-main">
                      {/* 第一行放**岗位**不放人名：这里选的是岗位，人名只是谁在演它。
                          反过来写会得到「郎静 · 总裁 · 经营会」这种两个点、三段式的读法。 */}
                      <span className="nm">{r.name}</span>
                      <span className="rl">{r.desc}</span>
                    </span>
                    <span className="acct-who">
                      {r.person} · <span className="mono">{a.user}</span>
                    </span>
                    <span className="ck" aria-hidden="true">{on ? <Icon name="check" size={14} /> : null}</span>
                  </button>
                );
              })}
            </div>

            <div className="formgrid rise rise-3">
              <label className="field">
                <span className="field-label">账号</span>
                <input value={u} onChange={(e) => setU(e.target.value)} autoComplete="username"
                  autoFocus required />
              </label>
              <label className="field">
                <span className="field-label">口令</span>
                <input type="password" value={p} onChange={(e) => setP(e.target.value)}
                  autoComplete="current-password" required />
              </label>
            </div>

            {err && <p className="t-body" role="alert" style={{ color: 'var(--red-text)' }}>
              <Icon name="warn" size={13} /> {err}
            </p>}

            <div className="rise rise-4">
              <Button type="submit" variant="primary" className="btn-block"
                loading={busy} loadingText="校验中…" disabled={busy || !u.trim()}>
                进入系统
              </Button>
            </div>

            <div className="rise rise-5 col" style={{ gap: 'var(--sp-3)' }}>
              <p className="t-aux">
                演示口令公开写在源码里（上线前必须全部更换）；点上面的岗位卡即自动填入。
              </p>
              {meta && (
                <details className="authmeta">
                  <summary className="t-aux">认证做到了什么、没做到什么</summary>
                  <dl className="deflist">
                    <dt>口令存储</dt><dd>{meta.password}</dd>
                    <dt>令牌</dt><dd>{meta.token} · 有效期 {meta.ttl_hours} 小时</dd>
                    <dt>权限校验</dt><dd>服务端按角色判定，前端那份只决定按钮显不显示</dd>
                  </dl>
                  <ul className="tickitems">
                    {meta.limits.map((l) => (
                      <li key={l}><Icon name="warn" size={13} /><span>{l}</span></li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
